import {
    getChartData,
    initialChartItem,
    LiquiditySummaryChartItem,
} from 'widgets/liquiditySummary/chart/liquiditySummaryChartData';
import { useEffect, useState } from 'react';
import {
    computePredictedRemainingVolume,
    getLiquiditySummaryData,
} from 'widgets/liquiditySummary/liquiditySummaryData';
import { getMax, Series, stackData } from 'utils/stackedSeries';
import { safeCalculatePercent } from 'utils/percent';
import { useExchangeRate } from 'app/hooks';
import { LiquiditySummaryDataState } from 'widgets/liquiditySummary/store/data/LiquiditySummaryDataState';
import { liquidityStreamedDataSelectors } from 'widgets/liquiditySummary/store/data/streamSlice';

export interface VolumeCapsuleData {
    historical: LiquiditySummaryChartItem;
    today: LiquiditySummaryChartItem;
    adv: number;
    todayAdvPercent: number;
    todayAdjPercent: number;
    stackedSeriesData: Series<VolumeCapsuleChartBar, string>[];
    maxX: number;
    todayTotal: number;
    historicalTotal: number;
    haveData: boolean;
}

const initialData: VolumeCapsuleData = {
    historical: initialChartItem,
    today: initialChartItem,
    adv: 0,
    todayAdvPercent: 0,
    todayAdjPercent: 0,
    stackedSeriesData: [],
    maxX: 0,
    todayTotal: 0,
    historicalTotal: 0,
    haveData: false,
};

export function useVolumeCapsuleData(): VolumeCapsuleData {
    const { useWidgetStreamData } = liquidityStreamedDataSelectors;
    const streamData = useWidgetStreamData();

    const exchangeRate = useExchangeRate();

    const [chartData, setChartData] = useState<VolumeCapsuleData>(initialData);

    useEffect(() => {
        const data = buildData(streamData, exchangeRate);
        setChartData(data.volumeCapsuleData);
    }, [streamData, exchangeRate]);

    return chartData;
}

export interface VolumeCapsuleChartBar extends LiquiditySummaryChartItem {
    bar: 'hist' | 'today';
    row: number;
}

function getStackedChartData(
    historical: LiquiditySummaryChartItem,
    today: LiquiditySummaryChartItem
): Series<VolumeCapsuleChartBar, string>[] {
    const barsData: VolumeCapsuleChartBar[] = [
        {
            bar: 'hist',
            row: 0,
            ...historical,
        },
        {
            bar: 'today',
            row: 0,
            ...today,
        },
    ];

    return stackData<VolumeCapsuleChartBar>(
        barsData,
        'open',
        'volume',
        'remainingVolume',
        'close'
    );
}

function itemTotal(historical: LiquiditySummaryChartItem) {
    return (
        historical.open +
        historical.volume +
        historical.remainingVolume +
        historical.close
    );
}

export function buildData(
    streamData: LiquiditySummaryDataState | undefined,
    exchangeRate: number | undefined
) {
    const liquiditySummaryData = getLiquiditySummaryData(
        streamData,
        exchangeRate ?? 1,
        1,
        1,
        'B'
    );

    const liquiditySummaryChartData = getChartData(liquiditySummaryData);

    const { historical, today } = liquiditySummaryChartData;
    const historicalTotal = itemTotal(historical);
    const todayTotal = itemTotal(today);

    const todayAdjPercent = liquiditySummaryData?.actualVsHistPct ?? 0;

    const todayAdvPercent = Math.round(
        safeCalculatePercent(todayTotal, historicalTotal)
    );

    const stackedSeriesData = getStackedChartData(historical, today);
    const maxX = getMax(stackedSeriesData);

    return {
        liquiditySummaryData,
        liquiditySummaryChartData,
        predictedRemainingVolume:
            computePredictedRemainingVolume(liquiditySummaryData),
        volumeCapsuleData: {
            adv: historicalTotal,
            todayAdvPercent,
            todayAdjPercent,
            historical,
            today,
            stackedSeriesData,
            maxX,
            historicalTotal,
            todayTotal,
            haveData: todayTotal !== 0 || historicalTotal !== 0,
        },
    };
}
